import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { formatCurrency } from "@/lib/money";
import { PAYMENT_METHOD_LABELS, type PaymentMethod } from "@/lib/paymentMethod";

export interface InvoiceDeliveryPromptModalProps {
  open: boolean;
  invoiceNumber: string;
  totalAmount: string;
  currency: string;
  paymentMethod: PaymentMethod;
  guestEmail: string | null;
  cashDrawerOpened: boolean;
  cashDrawerMessage: string | null;
  sendingEmail: boolean;
  openingPrint: boolean;
  actionError: string | null;
  includeXrechnung?: boolean;
  onIncludeXrechnungChange?: (value: boolean) => void;
  onSendEmail: () => void;
  onPrint: () => void;
  onSkip: () => void;
}

export function InvoiceDeliveryPromptModal({
  open,
  invoiceNumber,
  totalAmount,
  currency,
  paymentMethod,
  guestEmail,
  cashDrawerOpened,
  cashDrawerMessage,
  sendingEmail,
  openingPrint,
  actionError,
  includeXrechnung = false,
  onIncludeXrechnungChange,
  onSendEmail,
  onPrint,
  onSkip,
}: InvoiceDeliveryPromptModalProps) {
  const busy = sendingEmail || openingPrint;
  const hasEmail = Boolean(guestEmail?.trim());

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onSkip}
      title="Zahlung abgeschlossen"
      description="Zahlung erfolgreich verbucht. Wie soll die Rechnung zugestellt werden?"
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="secondary" disabled={busy} onClick={onSkip}>
            Ohne Versand fortfahren
          </Button>
          <Button type="button" variant="secondary" loading={openingPrint} disabled={busy} onClick={onPrint}>
            Rechnung drucken
          </Button>
          <Button
            type="button"
            loading={sendingEmail}
            disabled={busy || !hasEmail}
            onClick={onSendEmail}
            title={!hasEmail ? "Keine E-Mail-Adresse beim Gast hinterlegt" : undefined}
          >
            Per E-Mail senden
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="rounded-lg border border-separator bg-fill-tertiary p-4 text-sm">
          <p className="font-mono text-xs text-label-secondary">{invoiceNumber}</p>
          <p className="mt-1 tabular-nums">
            {formatCurrency(totalAmount, currency)} – {PAYMENT_METHOD_LABELS[paymentMethod]}
          </p>
        </div>

        {paymentMethod === "cash" ? (
          cashDrawerOpened ? (
            <p className="text-sm text-status-green">Kassenschublade wurde geöffnet.</p>
          ) : cashDrawerMessage ? (
            <p className="text-sm text-status-amber">{cashDrawerMessage}</p>
          ) : null
        ) : null}

        {hasEmail ? (
          <p className="text-sm text-label-secondary">
            E-Mail-Versand an <span className="font-medium text-label">{guestEmail}</span> als
            ZUGFeRD-PDF.
          </p>
        ) : (
          <p className="text-sm text-label-tertiary">
            Keine E-Mail-Adresse beim Gast hinterlegt – Versand per E-Mail nicht möglich.
          </p>
        )}

        {hasEmail && onIncludeXrechnungChange ? (
          <label className="flex items-start gap-2 text-sm text-label-secondary">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={includeXrechnung}
              onChange={(e) => onIncludeXrechnungChange(e.target.checked)}
              disabled={busy}
            />
            <span>Zusätzlich X-Rechnung (XML) anhängen</span>
          </label>
        ) : null}

        <p className="text-xs text-label-tertiary">
          „Rechnung drucken“ öffnet die PDF-Vorschau zum Ausdruck auf Blankopapier.
        </p>

        {actionError ? <p className="text-sm text-status-red">{actionError}</p> : null}
      </div>
    </Modal>
  );
}
